import { Link } from 'react-router-dom'
import { ArrowUpRight } from 'lucide-react'
import { CREATORS } from '../../constants/creators'

type Variant = 'full' | 'compact'
type Props = { variant?: Variant }

/** Roster strip shown on landing v2 (full) and reused for onboarding (compact).
 *  Renders all CREATORS plus a synthetic "+ one new voice every month" slot.
 *
 *  Consumes the full CREATORS list (including joining-soon entries like
 *  Jasleni). Practitioners with status === 'joining-soon' render faded with a
 *  "Joining soon" pill and no profile link. Practitioners without an `image`
 *  fall back to a single-letter initial avatar. */
export function PractitionersStrip({ variant = 'full' }: Props) {
  const isCompact = variant === 'compact'
  return (
    <section
      id="practitioners"
      className={`mx-auto max-w-[1100px] px-5 md:px-8 ${isCompact ? 'py-10' : 'py-20'}`}
    >
      {!isCompact && (
        <p className="text-xs uppercase tracking-[0.3em] text-text-muted">Our creators</p>
      )}
      <ul className={`mt-6 grid gap-6 ${isCompact ? 'grid-cols-2 md:grid-cols-4' : 'md:grid-cols-4'}`}>
        {CREATORS.filter((c) => c.status !== 'joining-soon').map((c) => {
          const joining = c.status === 'joining-soon'
          const initial = c.name.charAt(0)
          return (
            <li
              key={c.id}
              className={`rounded-lg border border-hairline bg-cosmic-card p-5 ${joining ? 'opacity-55' : ''}`}
            >
              <div className="flex items-center gap-3">
                {c.image ? (
                  <img
                    src={c.image}
                    alt=""
                    className="h-10 w-10 rounded-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <span className="grid h-10 w-10 place-items-center rounded-full bg-cosmic-surface text-text-secondary font-display text-base">
                    {initial}
                  </span>
                )}
                <div>
                  <p className="text-sm font-medium text-text-primary">{c.name}</p>
                  <p className="text-xs text-text-muted">{c.role}</p>
                </div>
              </div>
              {!isCompact && c.bio && <p className="mt-4 text-sm text-text-secondary line-clamp-3">{c.bio}</p>}
              {joining ? (
                <span className="mt-4 inline-block rounded-pill bg-cosmic-surface px-2 py-0.5 text-[10px] uppercase tracking-[0.3em] text-text-muted">
                  Joining soon
                </span>
              ) : (
                <Link
                  to={`/practitioners/${c.id}`}
                  className="mt-4 inline-flex items-center gap-1 text-xs text-gold hover:text-gold-light transition-colors"
                >
                  Read more <ArrowUpRight size={12} strokeWidth={1.75} />
                </Link>
              )}
            </li>
          )
        })}
        {/* Synthetic "+ one new voice every month" slot — sets the expectation
            that the roster grows on a steady cadence without committing to a
            specific next practitioner. */}
        <li className="rounded-lg border border-dashed border-hairline-strong bg-transparent p-5">
          <p className="font-display text-base italic text-text-secondary">
            More creators coming soon
          </p>
          {!isCompact && (
            <p className="mt-3 text-xs text-text-muted">
              New decks and rituals as the circle grows.
            </p>
          )}
        </li>
      </ul>
    </section>
  )
}
